import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'kenshin';
import {
  CloseOutlined,
  DownloadOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import './index.less';

const ImagePreview = ({
  images,
  initialIndex = 0,
  showSessionList = false,
  onClose,
  onAddToConversation,
  onDownload,
}) => {
  // 整个会话的图片列表（含所有会话里的图）
  const imageList = Array.isArray(images) ? images : [images];

  // 当前选中的图片索引
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // 缩放相关状态
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);

  const currentImage = imageList[currentIndex] || imageList[0];

  // 操作按钮配置
  const actionButtons = [
    {
      label: '添加到对话',
      icon: <PlusCircleOutlined />,
      action: () => {
        if (onAddToConversation) {
          onAddToConversation(currentImage);
          onClose();
        }
      },
      primary: true,
    },
    {
      label: '下载',
      icon: <DownloadOutlined />,
      action: () => {
        if (onDownload) {
          onDownload(currentImage);
        }
      },
    },
    // { label: '生成视频', icon: <VideoCameraOutlined />, action: () => {} },
    // { label: '超清', icon: <ExpandOutlined />, action: () => {} },
    // { label: 'AI改图', icon: <EditOutlined />, action: () => {} },
    // { label: '扩图', icon: <ExpandOutlined />, action: () => {} },
    // { label: '消除', icon: <EraserOutlined />, action: () => {} },
    // { label: '重新生成', icon: <RedoOutlined />, action: () => {} },
  ];

  // 主图容器 ref，用于绑定原生 wheel 监听
  const mainImageRef = useRef(null);

  // 鼠标滚轮缩放
  const handleWheel = useCallback((e) => {
    // React 的 onWheel 是 passive 监听，无法 preventDefault，
    // 这里通过原生非 passive 绑定来拦截页面滚动
    e.preventDefault();
    e.stopPropagation();

    const step = e.deltaY > 0 ? -0.1 : 0.1;
    // 限制缩放范围 0.5 ~ 5 倍
    const next = Math.min(5, Math.max(0.5, scaleRef.current + step));
    scaleRef.current = parseFloat(next.toFixed(2));
    setScale(scaleRef.current);
  }, []);

  // 用原生 addEventListener 绑定，避免 passive 报错
  useEffect(() => {
    const el = mainImageRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // 左侧列表容器 ref，用于自动滚动到高亮项
  const sessionListRef = useRef(null);

  // 点击缩略图切换主图
  const handleThumbClick = (idx) => {
    setCurrentIndex(idx);
    // 切换图片时重置缩放
    scaleRef.current = 1;
    setScale(1);
  };

  // 打开弹窗或切换图片时，让左侧列表滚动到高亮项
  useEffect(() => {
    const list = sessionListRef.current;
    if (!list) return;
    const active = list.querySelector('.sessionThumbItem.active');
    if (!active) return;
    const itemTop = active.offsetTop;
    const itemBottom = itemTop + active.offsetHeight;
    // 当高亮项超出可视区域才滚动，保证能看到
    if (itemTop < list.scrollTop) {
      list.scrollTop = itemTop;
    } else if (itemBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = itemBottom - list.clientHeight;
    }
  }, [currentIndex, imageList.length]);

  return (
    <div className="imagePreviewOverlay" onClick={onClose}>
      <div className="imagePreviewContent" onClick={(e) => e.stopPropagation()}>
        {/* 关闭按钮 */}
        <div className="closeBtn" onClick={onClose}>
          <CloseOutlined />
        </div>

        {/* 左侧：整个会话的图片列表（仅点击 AI 回复图片时显示） */}
        {showSessionList && (
          <div className="sessionPanel">
            <div className="sessionPanelHeader">会话图片</div>
            <div className="sessionThumbList" ref={sessionListRef}>
              {imageList.map((img, idx) => (
                <div
                  key={idx}
                  className={`sessionThumbItem ${
                    idx === currentIndex ? 'active' : ''
                  }`}
                  onClick={() => handleThumbClick(idx)}
                >
                  <img src={img} alt="" />
                  <span className="sessionThumbIndex">{idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 主图 */}
        <div className="mainImage" ref={mainImageRef}>
          <img
            src={currentImage}
            alt="预览图片"
            style={{
              transform: `scale(${scale})`,
              transition: 'transform 0.15s ease',
            }}
          />
          {scale !== 1 && (
            <div className="zoomTip">{Math.round(scale * 100)}%</div>
          )}
        </div>

        {/* 右侧操作区 */}
        <div className="sidePanel">
          {/* 缩略图列表：用户上传的图片用这一块 */}
          {!showSessionList && imageList.length > 0 && (
            <div className="thumbList">
              {imageList.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt=""
                  className={`thumbItem ${
                    idx === currentIndex ? 'active' : ''
                  }`}
                  onClick={() => handleThumbClick(idx)}
                />
              ))}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="actionButtons">
            {actionButtons.map((btn, idx) => (
              <Button
                key={idx}
                className={`actionBtn ${btn.primary ? 'actionBtnPrimary' : ''}`}
                onClick={btn.action}
                style={{ marginLeft: 0 }}
                icon={btn.icon}
              >
                {btn.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePreview;
